import { useQuery } from "@tanstack/react-query";
import { useCursorPagination } from "_/hooks/useCursorPagination";
import {
  api,
  ApiResponse,
  type Page,
  type PageQuery,
  PageResult,
  indexById,
} from "_/utils";
import { Uuid } from "_/types";

const SCOPE = "buildsets";

export const KEYS = {
  all: () => [SCOPE],
  page: (search?: string) => [SCOPE, "page", search],
  buildset: (id: Uuid) => [SCOPE, id],
  manifest: (id: Uuid) => [SCOPE, id, "manifest"],
};

export type Buildset = {
  /**
   * Build set ID, which is a unique identifier for the build.
   */
  id: Uuid;

  /**
   * The display version of the build, which is a human-readable string.
   */
  displayVersion: string;

  /**
   * The display name of the build, which is a human-readable name for the build and may be
   * edited or modified to meet the display needs for end-users and within the Basis UI.
   */
  displayName: string;

  /**
   * The timestamp from when the build was created.
   */
  buildTime: string;

  /**
   * URL to the GitHub action workflow run for the build.
   */
  buildUrl: string;

  /**
   * Git commit SHA for the build. This should not be used as a unique identifier, but instead
   * is used to identify the source code used for the build.
   */
  gitSha: string;

  /**
   * The Git ref(branch, tag, or other reference) that the build was created from.
   */
  gitRef: string;

  /// Total count of machines currently using this build set.
  targetMachines: number;
};

export type BuildsetIdMap = Record<Uuid, Buildset>;

export type BuildsetArtifact = {
  url: string;
  size?: number;
  sha256?: string;
};

export type BuildsetManifest = {
  artifacts: {
    raucb?: BuildsetArtifact;
    iso?: BuildsetArtifact;
    sbom?: BuildsetArtifact;
    notes?: BuildsetArtifact;
  };
  buildId: string;
  buildTimestamp: string;
  buildUrl: string;
  displayName: string;
  displayVersion: string;
  gitRef: string;
  gitSha: string;
};

type BuildsetsPageQuery = PageQuery & {
  search?: string;
};

export async function getBuildsetsPage(
  query: BuildsetsPageQuery
): Promise<Page<Buildset>> {
  const response = await api.get<PageResult<Buildset>>("/buildsets", {
    params: query,
  });
  return response.data.data;
}

export async function getBuildsets(): Promise<Buildset[]> {
  const page = await getBuildsetsPage({ limit: 200 });
  return page.items;
}

export function useBuildsets() {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn: getBuildsets,
  });
}

export function useBuildsetsPage(search?: string) {
  const normalizedSearch = search?.trim() || undefined;

  return useCursorPagination({
    queryKey: KEYS.page(normalizedSearch),
    getPage: (query) =>
      getBuildsetsPage({ ...query, search: normalizedSearch }),
    pageSize: 100,
  });
}

export async function getBuildset(id: Uuid): Promise<Buildset> {
  const response = await api.get<ApiResponse<Buildset>>(`/buildsets/${id}`);
  return response.data.data;
}

export function useBuildset(id: Uuid) {
  return useQuery({
    queryKey: KEYS.buildset(id),
    queryFn: () => getBuildset(id),
  });
}

export function buildBuildsetIdMap(
  buildsets: Buildset[]
): Record<Uuid, Buildset> {
  return indexById(buildsets);
}

export function getBuildsetUrls(
  id?: Uuid,
  displayVersion?: string
): {
  manifestUrl: string | undefined;
  isoUrl: string | undefined;
  raucbUrl: string | undefined;
} {
  if (!id || !displayVersion) {
    return { manifestUrl: undefined, isoUrl: undefined, raucbUrl: undefined };
  }
  const base = `${api.defaults.baseURL}/buildsets/${id}/artifacts`;
  return {
    manifestUrl: `${base}/manifest.json`,
    isoUrl: `${base}/${displayVersion}.iso`,
    raucbUrl: `${base}/${displayVersion}.raucb`,
  };
}

export async function getBuildsetManifest(id: Uuid): Promise<BuildsetManifest> {
  const response = await api.get<BuildsetManifest>(
    `/buildsets/${id}/artifacts/manifest.json`
  );
  return response.data;
}

export function useBuildsetManifest(id: Uuid) {
  return useQuery({
    queryKey: KEYS.manifest(id),
    queryFn: () => getBuildsetManifest(id),
    enabled: Boolean(id),
  });
}
